import { Plus, Store } from 'lucide-react'

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'

import { Permission, Role } from '@/types'

import { useTranslation } from 'next-i18next'
import useAuth from '@/contexts/useAuth'
import Link from 'next/link'
import Image from 'next/image'
import { Button } from '../ui/button'
import { usePathname } from 'next/navigation'
import { keyBy } from '@/lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { UserService } from '@/lib/services/user'
import { useToast } from '@/hooks/use-toast'
import { sidebarItems } from '@/types/sidebar'

const getItems = (permissions: Permission[], isStripeIndependent: boolean) => {
  const permissionsByKey = keyBy(permissions)

  let authorizedGroups: Record<string, any> = {}
  if (permissionsByKey[Permission.BookNowInternal]) {
    authorizedGroups.booking = []
  }

  Object.keys(sidebarItems).forEach((group) => {
    const authorizedItems = sidebarItems[group].filter((item: any) => {
      if (isStripeIndependent && item.title === 'external_experiences') {
        return false
      }

      return !item.permission || permissionsByKey[item.permission]
    })

    if (isStripeIndependent && group === 'external_experiences') {
      return false
    }

    if (authorizedItems.length > 0) {
      authorizedGroups[group] = authorizedItems
    }
  })

  return authorizedGroups
}

export function AppSidebar() {
  const { t } = useTranslation('common')
  const { user } = useAuth()
  const pathname = usePathname()
  const { toast } = useToast()

  const items = getItems(
    user?.permissions ?? [],
    user?.company?.hasIndependentStripe ?? false
  )

  const switchCompany = async (companyId: number) => {
    if (!user) {
      return
    }
    try {
      await UserService.switchAdminCompany(user, companyId)
      toast({ title: t('common:save_success'), variant: 'success' })

      window.location.reload()
    } catch (error: any) {
      console.error(error)
      toast({ description: error.message, variant: 'destructive' })
    }
  }

  return (
    <Sidebar collapsible="icon">
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>
            <div className="pt-8">
              <Link className="logo flex justify-center" href="/">
                <span className="mr-2 text-4xl font-extrabold text-primary group-data-[collapsible=icon]:hidden">
                  ALOE
                </span>
              </Link>
            </div>
          </SidebarGroupLabel>
        </SidebarGroup>

        {user?.role === Role.Admin && (
          <Select
            value={user.companyId?.toString()}
            onValueChange={(value) => switchCompany(Number(value))}
          >
            <SelectTrigger className="border-none shadow-none focus:ring-0 focus:ring-ring hover:bg-accent px-4">
              <SelectValue placeholder="" />
            </SelectTrigger>
            <SelectContent>
              {user.manageableCompanies?.map(({ label, value }) => (
                <SelectItem value={value.toString()} key={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {Object.keys(items).map((group) =>
          group === 'booking' ? (
            <div
              className="px-4 mt-8 group-data-[collapsible=icon]:px-2"
              key="booking"
            >
              <Link href="/create-order">
                <Button className="w-full justify-between group-data-[collapsible=icon]:justify-center">
                  <span className="uppercase group-data-[collapsible=icon]:hidden">
                    {t('book_now')}
                  </span>

                  <Plus size={24} />
                </Button>
              </Link>
            </div>
          ) : (
            <SidebarGroup key={group}>
              <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">
                <div className="mb-2 text-lg font-semibold tracking-tight text-black capitalize">
                  {t(group)}
                </div>
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {items[group].map((item: any) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        isActive={
                          item.url === '/'
                            ? pathname === '/'
                            : pathname.length > 1 &&
                              pathname.startsWith(item.url.split('?')[0])
                        }
                      >
                        {user?.role === Role.Organizer &&
                        item.title === 'internal_experiences' ? (
                          <Link href={item.url}>
                            <Store />
                            <span>{t('online_stores')}</span>
                          </Link>
                        ) : (
                          <Link href={item.url}>
                            <item.icon />
                            <span>{t(item.title)}</span>
                          </Link>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem></SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
